<template>
  <div class="page-container animate-fade-in">
    <PageHeader
      title="Add Project"
      description="Upload and configure a new portfolio project."
      :breadcrumbs="[
        { label: 'Admin', to: '/admin' },
        { label: 'Portfolio', to: '/admin/portfolio' },
        { label: 'Add Project' },
      ]"
    />

    <BaseCard>
      <form class="space-y-6" @submit.prevent="handleSubmit">
        <div class="grid lg:grid-cols-3 gap-8">
          <div>
            <p class="text-label mb-3">Project File (optional)</p>
            <FileDropzone accept=".jpg,.png,.gif,.zip,.rar,.apk" hint="Max 10MB" @file-selected="handleFilesSelected" />
          </div>

          <div class="lg:col-span-2 space-y-5">
            <BaseInput v-model="form.projectName" label="Project Name" placeholder="Enter project name" required />
            <div>
              <label class="text-label block mb-1.5">Project Type</label>
              <select
                v-model="form.projectType"
                class="w-full rounded-lg border border-border bg-surface px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
              >
                <option value="">Select type</option>
                <option value="web">Web</option>
                <option value="mobile">Mobile</option>
                <option value="system">System</option>
              </select>
            </div>
            <div>
              <label class="text-label block mb-1.5">File type</label>
              <select
                v-model="form.fileType"
                class="w-full rounded-lg border border-border bg-surface px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
              >
                <option value="zip">ZIP</option>
                <option value="rar">RAR</option>
                <option value="apk">APK</option>
              </select>
            </div>
            <BaseInput v-model="form.repository" label="Repository URL" placeholder="https://github.com/..." />
            <BaseInput v-model="form.liveUrl" label="Live / file URL" placeholder="https://..." />
            <BaseTextarea v-model="form.description" label="Description" placeholder="Describe the project..." :rows="4" />
            <label class="flex items-center gap-2 text-sm">
              <input v-model="form.publish" type="checkbox" class="rounded border-border text-primary focus:ring-primary" />
              Publish on portfolio
            </label>
          </div>
        </div>

        <div class="flex justify-end gap-3 pt-4 border-t border-border-subtle">
          <BaseButton variant="secondary" type="button" tag="router-link" to="/admin/portfolio">Cancel</BaseButton>
          <BaseButton variant="primary" type="submit" :loading="isLoading">Save Project</BaseButton>
        </div>
      </form>
    </BaseCard>
  </div>
</template>

<script>
import { siteState, saveSiteSection } from '@/composables/useSiteData'
import { useToast } from '@/composables/useToast'
import PageHeader from '@/components/layout/PageHeader.vue'
import BaseCard from '@/components/ui/BaseCard.vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import BaseTextarea from '@/components/ui/BaseTextarea.vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import FileDropzone from '@/components/ui/FileDropzone.vue'

export default {
  name: 'AddPortfolio',
  components: { PageHeader, BaseCard, BaseInput, BaseTextarea, BaseButton, FileDropzone },
  data() {
    return {
      isLoading: false,
      form: {
        projectType: '',
        projectName: '',
        fileType: 'zip',
        repository: '',
        liveUrl: '',
        description: '',
        publish: false,
      },
      selectedFile: null,
    }
  },
  methods: {
    handleFilesSelected(file) {
      this.selectedFile = file
    },
    async handleSubmit() {
      if (!this.form.projectName || !this.form.projectType) return
      this.isLoading = true
      const { success, error } = useToast()
      try {
        const newItem = {
          id: Date.now(),
          file_type: this.form.fileType,
          repository: this.form.repository,
          file_name: this.form.projectName,
          file_url: this.form.liveUrl,
          host_url: this.form.liveUrl,
          category: this.form.projectType,
          publish: this.form.publish,
          description: this.form.description,
        }
        siteState.portfolioItems.push(newItem)
        await saveSiteSection('portfolioItems', siteState.portfolioItems)
        success('Project saved', 'Portfolio item has been added.')
        this.$router.push('/admin/portfolio')
      } catch (e) {
        error('Save failed', 'Could not save portfolio item.')
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
